import { tokenStore } from '@/lib/auth/tokenStore'
import { getPortalApiBase } from '@/lib/env'

/**
 * Cliente del API admin de pasarelas visibles (tabla propia del portal-server,
 * pagos-zeus-3). A diferencia de los endpoints admin viejos, estos exigen
 * Bearer real (Identidad valida la firma) + membresía en admin_users.
 */
const ENDPOINT = `${getPortalApiBase()}/admin/payment-gateways`

/** Tope del nombre público (espejo de MAX_PUBLIC_NAME_LENGTH del portal-server). */
export const MAX_PUBLIC_NAME_LENGTH = 60

export interface AdminPaymentGateway {
  id: number
  /** El nombre REAL de Zeus (back-office). Nunca se edita desde el portal. */
  name: string
  code: string
  enabled: boolean
  /** El nombre que ve el cliente al pagar; `null` = usa el de Zeus. */
  publicName: string | null
}

function authHeaders(): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function json<T>(res: Response, ctx: string): Promise<T> {
  if (!res.ok) throw new Error(`${ctx}: ${res.status}`)
  const text = await res.text()
  return (text.trim() ? JSON.parse(text) : null) as T
}

/** Externas de Zeus con su flag de visibilidad en el portal. */
export async function fetchAdminPaymentGateways(): Promise<
  AdminPaymentGateway[]
> {
  const res = await fetch(ENDPOINT, { headers: authHeaders() })
  return (await json<AdminPaymentGateway[] | null>(res, 'GET payment-gateways')) ?? []
}

/** Habilita/deshabilita una pasarela para el autoservicio del portal. */
export async function setPaymentGatewayEnabled(
  zeusId: number,
  enabled: boolean,
): Promise<{ id: number; enabled: boolean }> {
  const res = await fetch(`${ENDPOINT}/${zeusId}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
  return json<{ id: number; enabled: boolean }>(res, 'PUT payment-gateways/:id')
}

/**
 * Fija el nombre público de una pasarela (el que ve el cliente al pagar).
 * Endpoint APARTE del de visibilidad: renombrar no enciende ni apaga nada.
 * `null` (o vacío) = volver al nombre de Zeus.
 */
export async function setPaymentGatewayPublicName(
  zeusId: number,
  publicName: string | null,
): Promise<{ id: number; publicName: string | null }> {
  const res = await fetch(`${ENDPOINT}/${zeusId}/public-name`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ publicName }),
  })
  return json<{ id: number; publicName: string | null }>(
    res,
    'PUT payment-gateways/:id/public-name',
  )
}
